import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Check, ArrowRight, Loader2, GraduationCap, Users, Building2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PricingPage() {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/packages?status=active`);
      setPackages(response.data);
    } catch (error) {
      console.error('Error fetching packages:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get one package per role type (first active one found)
  const studentPackage = packages.find(p => p.package_type === 'student');
  const agentPackage = packages.find(p => p.package_type === 'agent');
  const organizationPackage = packages.find(p => p.package_type === 'organization');

  // Role icons and colors
  const roleConfig = {
    student: {
      icon: GraduationCap,
      title: 'Student',
      color: 'lime',
      description: 'Perfect for individual students preparing for interviews'
    },
    agent: {
      icon: Users,
      title: 'Education Agent',
      color: 'blue',
      description: 'For education consultants managing multiple students'
    },
    organization: {
      icon: Building2,
      title: 'Organization',
      color: 'purple',
      description: 'For universities and large institutions'
    }
  };

  // Fallback packages if none exist in database
  const fallbackPackages = {
    student: {
      id: 'fallback-student',
      name: 'Student Plan',
      description: 'Perfect for individual students',
      price: 29,
      billing_cycle: 'one-time',
      included_credits: 3,
      package_type: 'student',
      features: ['3 Interview Credits', 'Full AI Assessment', 'Detailed Feedback Report', 'Video Recording Access', 'Transcript Download']
    },
    agent: {
      id: 'fallback-agent',
      name: 'Agent Plan',
      description: 'For education consultants',
      price: 199,
      billing_cycle: 'one-time',
      included_credits: 25,
      package_type: 'agent',
      features: ['25 Interview Credits', 'Student Management Dashboard', 'Bulk Interview Scheduling', 'Performance Analytics', 'Priority Support']
    },
    organization: {
      id: 'fallback-organization',
      name: 'Organization Plan',
      description: 'For institutions',
      price: 499,
      billing_cycle: 'one-time',
      included_credits: 100,
      package_type: 'organization',
      features: ['100 Interview Credits', 'Unlimited Team Members', 'Custom Question Bank', 'Advanced Analytics', 'Dedicated Support', 'White-label Option']
    }
  };

  // Use API packages or fallback
  const displayPackages = [
    studentPackage || fallbackPackages.student,
    agentPackage || fallbackPackages.agent,
    organizationPackage || fallbackPackages.organization
  ];

  const handleSelectPackage = (pkg) => {
    const params = new URLSearchParams({
      package_id: pkg.id,
      package_name: pkg.name,
      package_type: pkg.package_type,
      price: pkg.price.toString(),
      credits: pkg.included_credits.toString()
    });
    navigate(`/register?${params.toString()}`);
  };

  const getCardStyles = (type, index) => {
    // Middle card (Agent) gets highlighted
    const isHighlighted = index === 1;
    
    if (isHighlighted) {
      return {
        card: 'border-lime-500 shadow-xl ring-2 ring-lime-500/20 scale-105 z-10',
        button: 'bg-lime-500 hover:bg-lime-600 text-white',
        badge: true
      };
    }
    return {
      card: 'border-border/50 hover:border-lime-300 hover:shadow-lg',
      button: '',
      badge: false
    };
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1 pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-lime-100 text-lime-700 dark:bg-lime-900/30 dark:text-lime-400">
              Pricing
            </Badge>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tight mb-4">
              Simple, Transparent Pricing
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Choose the plan that fits your needs. All plans include full access to our AI-powered interview platform.
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-lime-500" />
              <span className="ml-3 text-muted-foreground">Loading packages...</span>
            </div>
          ) : (
            <>
              {/* Pricing Cards - One per role */}
              <div className="grid md:grid-cols-3 gap-6 lg:gap-8 items-center max-w-5xl mx-auto">
                {displayPackages.map((pkg, index) => {
                  const config = roleConfig[pkg.package_type];
                  const styles = getCardStyles(pkg.package_type, index);
                  const Icon = config.icon;
                  
                  return (
                    <Card 
                      key={pkg.id} 
                      className={`relative transition-all duration-300 ${styles.card}`}
                      data-testid={`pricing-card-${pkg.package_type}`}
                    >
                      {styles.badge && (
                        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-lime-500 text-white px-4 shadow-lg">
                          Most Popular
                        </Badge>
                      )}
                      
                      <CardHeader className="text-center pb-2">
                        {/* Role Icon */}
                        <div className={`w-14 h-14 mx-auto rounded-2xl flex items-center justify-center mb-4 ${
                          index === 1 
                            ? 'bg-lime-500 text-white' 
                            : 'bg-lime-100 dark:bg-lime-900/30 text-lime-600 dark:text-lime-400'
                        }`}>
                          <Icon className="w-7 h-7" />
                        </div>
                        <CardTitle className="font-heading text-xl">{config.title}</CardTitle>
                        <CardDescription className="text-sm">{config.description}</CardDescription>
                      </CardHeader>
                      
                      <CardContent className="text-center pb-6">
                        {/* Price */}
                        <div className="my-6">
                          <div className="flex items-baseline justify-center gap-1">
                            <span className="text-5xl font-heading font-bold">£{pkg.price}</span>
                            {pkg.billing_cycle !== 'one-time' && (
                              <span className="text-muted-foreground text-lg">/{pkg.billing_cycle === 'monthly' ? 'mo' : 'yr'}</span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mt-2">
                            {pkg.included_credits} interview credit{pkg.included_credits !== 1 ? 's' : ''} included
                          </p>
                        </div>
                        
                        {/* Features */}
                        {pkg.features && pkg.features.length > 0 && (
                          <ul className="space-y-3 text-left">
                            {pkg.features.map((feature, i) => (
                              <li key={i} className="flex items-start gap-3 text-sm">
                                <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                  index === 1 
                                    ? 'bg-lime-500 text-white' 
                                    : 'bg-lime-100 dark:bg-lime-900/30 text-lime-600 dark:text-lime-400'
                                }`}>
                                  <Check className="w-3 h-3" />
                                </div>
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </CardContent>
                      
                      <CardFooter>
                        <Button 
                          className={`w-full gap-2 rounded-full ${styles.button}`}
                          variant={index === 1 ? 'default' : 'outline'}
                          size="lg"
                          onClick={() => handleSelectPackage(pkg)}
                          data-testid={`pricing-cta-${pkg.package_type}`}
                        >
                          Get Started
                          <ArrowRight className="w-4 h-4" />
                        </Button>
                      </CardFooter>
                    </Card>
                  );
                })}
              </div>

              {/* Features comparison or trust badges */}
              <div className="mt-16 text-center">
                <p className="text-sm text-muted-foreground mb-6">All plans include</p>
                <div className="flex flex-wrap justify-center gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-lime-500" />
                    <span>AI-Powered Analysis</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-lime-500" />
                    <span>Video Recording</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-lime-500" />
                    <span>Detailed Feedback</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-lime-500" />
                    <span>Proctoring Analytics</span>
                  </div>
                </div>
              </div>

              {/* Custom Pricing CTA */}
              <div className="mt-16 text-center bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-900/50 dark:to-slate-800/30 rounded-2xl p-8 max-w-3xl mx-auto">
                <h2 className="font-heading font-bold text-2xl mb-4">
                  Need a Custom Solution?
                </h2>
                <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
                  For universities, large organizations, or custom requirements, we offer tailored pricing plans with volume discounts.
                </p>
                <Button 
                  variant="outline" 
                  className="gap-2 rounded-full"
                  onClick={() => navigate('/contact')}
                  data-testid="contact-sales-btn"
                >
                  Contact Sales
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
